<script>
import Icon from '@/components/Icon'

const SECTIONS = [
  { id: 'grupo', field: 'grupo_nome', label: 'Grupo' },
  { id: 'tipo', field: 'grupo_tipo', label: 'Tipo' },
  { id: 'familia', field: 'grupo_familia', label: 'Família' },
  { id: 'marca', field: 'marca_nome', label: 'Marca' },
  { id: 'linha', field: 'linha_nome', label: 'Linha' }
]

const EMPTY_FILTERS = {
  grupo_nome: '',
  grupo_tipo: '',
  grupo_familia: '',
  marca_nome: '',
  linha_nome: '',
  descricao_curta: '',
  aumentarRelevancia: '',
}

const TOP_N_VISIBLE = 10
const ALWAYS_EXPAND_THRESHOLD = 15

export default {
  name: 'ProductFilters',

  components: {
    Icon,
  },

  props: {
    filters: {
      type: Object,
      default: () => ({ ...EMPTY_FILTERS }),
    },
    aggregations: {
      type: Object,
      default: null,
    },
    // Exibe o botão "voltar" no header — usado só no drawer mobile/tablet.
    showClose: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      localFilters: { ...EMPTY_FILTERS },
      openSections: {
        grupo: true,
        tipo: true,
        familia: true,
        marca: true,
        linha: true,
      },
      sectionSearches: {
        grupo: '',
        tipo: '',
        familia: '',
        marca: '',
        linha: '',
      },
    }
  },

  computed: {
    sections () {
      return SECTIONS.map(s => {
        const rawBuckets = (this.aggregations && this.aggregations[s.field] && Array.isArray(this.aggregations[s.field].buckets))
          ? this.aggregations[s.field].buckets
          : []
        // Filtra buckets sem nome (ex.: { nome: "", sku: 6, docCount: 6 }) — vêm vazios do FTS quando o campo não foi indexado no documento.
        const buckets = rawBuckets.filter(b => b && b.nome != null && String(b.nome).trim() !== '')
        return {
          ...s,
          buckets,
          value: this.localFilters[s.field],
        }
      })
    },

    hasAnyOption () {
      return this.sections.some(s => s.buckets.length > 0)
    },
  },

  watch: {
    filters: {
      handler (newVal) {
        if (newVal) {
          this.localFilters = { ...EMPTY_FILTERS, ...newVal }
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    emitFilters () {
      this.$emit('update-filters', { ...this.localFilters })
    },

    selectOption (field, bucketName) {
      this.localFilters[field] = this.localFilters[field] === bucketName ? '' : bucketName
      this.emitFilters()
    },

    updateFreeField (field, value) {
      this.localFilters[field] = value
      this.emitFilters()
    },

    clearAll () {
      this.localFilters = { ...EMPTY_FILTERS }
      Object.keys(this.sectionSearches).forEach(k => {
        this.sectionSearches[k] = ''
      })
      this.emitFilters()
    },

    toggleSection (id) {
      this.$set(this.openSections, id, !this.openSections[id])
    },

    isSelected (field, bucketName) {
      return this.localFilters[field] === bucketName
    },

    sectionCount (section) {
      return section.value && String(section.value).trim() !== '' ? 1 : 0
    },

    /** Buckets filtrados pela busca da seção (case insensitive) */
    filteredBuckets (section) {
      const search = (this.sectionSearches[section.id] || '').toLowerCase().trim()
      if (!search) return section.buckets
      return section.buckets.filter(b =>
        String(b.nome).toLowerCase().includes(search)
      )
    },

    /**
     * Buckets a renderizar:
     * - Se total <= ALWAYS_EXPAND_THRESHOLD, mostra todos
     * - Se há busca ativa, mostra todos os matches
     * - Senão, mostra apenas Top N + sinaliza restantes
     */
    visibleBuckets (section) {
      const filtered = this.filteredBuckets(section)
      const totalAvailable = section.buckets.length
      const hasSearch = !!(this.sectionSearches[section.id] || '').trim()

      if (totalAvailable <= ALWAYS_EXPAND_THRESHOLD || hasSearch) {
        return filtered
      }
      return filtered.slice(0, TOP_N_VISIBLE)
    },

    /** Quantos buckets ficaram ocultos (apenas quando não há busca) */
    hiddenCount (section) {
      const hasSearch = !!(this.sectionSearches[section.id] || '').trim()
      if (hasSearch) return 0
      if (section.buckets.length <= ALWAYS_EXPAND_THRESHOLD) return 0
      return section.buckets.length - TOP_N_VISIBLE
    },

    /** Se deve mostrar o input de busca da seção */
    showSectionSearch (section) {
      return section.buckets.length > ALWAYS_EXPAND_THRESHOLD
    },

    /** Label de pluralização para o contador de "outras opções" */
    hiddenLabel (section) {
      const count = this.hiddenCount(section)
      if (count <= 0) return ''
      const labelLower = section.label.toLowerCase()
      return `+${count} outras ${labelLower}s — use a busca acima`
    },
  },
}
</script>

<template>
  <div :class="$classes['filters-card']">
    <header :class="[$classes['filters-header'], { [$classes['filters-header-drawer']]: showClose }]">
      <div :class="$classes['filters-header-left']">
        <button v-if="showClose" type="button" :class="$classes['filters-back-btn']" title="Voltar"
          @click="$emit('close')">
          <icon name="long-arrow-alt-left" />
        </button>
        <icon v-else name="filter-horizontal" :class="$classes['filters-header-icon']" />
        <span :class="$classes['filters-header-title']">Filtros</span>
      </div>
      <button type="button" :class="$classes['clear-link']" @click="clearAll">
        Limpar
      </button>
    </header>

    <div :class="$classes['filters-body']">
      <section v-for="section in sections" :key="section.id" :class="$classes['filter-section']">
        <button type="button" :class="$classes['section-header']" @click="toggleSection(section.id)">
          <span :class="$classes['section-label']">
            <span>{{ section.label }}</span>
            <span v-if="sectionCount(section)" :class="$classes['section-count']">
              ({{ sectionCount(section) }})
            </span>
          </span>
          <icon :name="openSections[section.id] ? 'caret-up' : 'caret-down'" :class="$classes['section-caret']" />
        </button>

        <div v-show="openSections[section.id]" :class="$classes['section-body']">
          <p v-if="section.buckets.length === 0" :class="$classes['section-empty']">
            Sem opções disponíveis
          </p>

          <template v-else>
            <input v-if="showSectionSearch(section)" v-model="sectionSearches[section.id]" type="text"
              :placeholder="`Buscar ${section.label.toLowerCase()}...`" :class="$classes['section-search']" />

            <label v-for="bucket in visibleBuckets(section)" :key="bucket.nome"
              :class="[$classes['option-row'], { [$classes['option-row-selected']]: isSelected(section.field, bucket.nome) }]">
              <span
                :class="[$classes['checkbox'], { [$classes['checkbox-checked']]: isSelected(section.field, bucket.nome) }]"
                @click.prevent="selectOption(section.field, bucket.nome)">
                <svg v-if="isSelected(section.field, bucket.nome)" viewBox="0 0 16 16"
                  :class="$classes['checkbox-icon']">
                  <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </span>
              <span :class="$classes['option-text']" @click.prevent="selectOption(section.field, bucket.nome)">
                {{ bucket.nome }}
              </span>
              <span :class="$classes['option-count']" @click.prevent="selectOption(section.field, bucket.nome)">
                {{ bucket.sku }}
              </span>
            </label>

            <p v-if="hiddenCount(section) > 0" :class="$classes['section-hint']">
              {{ hiddenLabel(section) }}
            </p>

            <p v-if="showSectionSearch(section) && filteredBuckets(section).length === 0"
              :class="$classes['section-empty']">
              Nenhum resultado
            </p>
          </template>
        </div>
      </section>

      <!-- <section :class="[$classes['filter-section'], $classes['filter-section-free']]">
        <label :class="$classes['free-label']">Descrição curta</label>
        <input
          :value="localFilters.descricao_curta"
          type="text"
          placeholder="Filtrar por palavra"
          :class="$classes['free-input']"
          @input="updateFreeField('descricao_curta', $event.target.value)" />

        <label :class="$classes['free-label']">Aumentar relevância</label>
        <input
          :value="localFilters.aumentarRelevancia"
          type="text"
          placeholder="Strings que aumentarão o peso"
          :class="$classes['free-input']"
          @input="updateFreeField('aumentarRelevancia', $event.target.value)" />
      </section> -->
    </div>
  </div>
</template>

<style module>
.filters-card {
  background: #ffffff;
  border: 1px solid #ECECEE;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.filters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #ECECEE;
}

.filters-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3D3D3D;
}

.filters-header-icon {
  width: 14px;
  height: 14px;
  color: #3D3D3D;
}

.filters-header-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3D3D3D;
}

/* Botão "voltar" — aparece só no modo drawer (mobile/tablet) */
.filters-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  color: #3D3D3D;
  cursor: pointer;
  transition: color 180ms cubic-bezier(.2, .7, .3, 1);
}

.filters-back-btn:hover {
  color: #1A1A1A;
}

.filters-back-btn svg {
  width: 20px;
  height: 20px;
}

/* Header no modo drawer: voltar + "Filtros" maior, com gap de 12px */
.filters-header-drawer .filters-header-left {
  gap: 12px;
}

.filters-header-drawer .filters-header-title {
  font-size: 16px;
  letter-spacing: 0.04em;
}

.clear-link {
  background: none;
  border: none;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #962525;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 180ms cubic-bezier(.2, .7, .3, 1);
}

.clear-link:hover {
  background: #FDF2F2;
}

.filters-body {
  padding: 4px 16px 16px;
  overflow-y: auto;
  max-height: calc(100vh - 240px);
}

.filter-section {
  border-bottom: 1px solid #ECECEE;
  padding: 12px 0;
}

.filter-section:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-bottom: 8px;
}

.section-label {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3D3D3D;
}

.section-count {
  color: #D14040;
  font-weight: 700;
}

.section-caret {
  width: 12px;
  height: 12px;
  color: #7A7A7A;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.section-search {
  width: 100%;
  padding: 6px 10px;
  margin-bottom: 6px;
  border: 1px solid #E2E2E2;
  border-radius: 4px;
  font-size: 12px;
  color: #1A1A1A;
  outline: none;
  transition: border-color 180ms cubic-bezier(.2, .7, .3, 1), box-shadow 180ms cubic-bezier(.2, .7, .3, 1);
}

.section-search:focus {
  border-color: #D14040;
  box-shadow: 0 0 0 3px rgba(209, 64, 64, 0.15);
}

.section-search::placeholder {
  color: #A1A1A1;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  cursor: pointer;
  border-radius: 4px;
  transition: background 120ms cubic-bezier(.2, .7, .3, 1);
}

.option-row:hover {
  background: #F8F8F9;
}

.option-row-selected {
  color: #1A1A1A;
}

.checkbox {
  width: 16px;
  height: 16px;
  border: 1.5px solid #C7C7C7;
  border-radius: 3px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 120ms cubic-bezier(.2, .7, .3, 1), background 120ms cubic-bezier(.2, .7, .3, 1);
}

.option-row:hover .checkbox {
  /* border-color: #7A7A7A; */
}

.checkbox-checked {
  background: #D14040;
  border-color: #D14040;
}

.checkbox-icon {
  width: 12px;
  height: 12px;
  color: #ffffff;
}

.option-text {
  flex: 1;
  font-size: 12px;
  color: #292929;
  line-height: 1.4;
  user-select: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-row-selected .option-text {
  color: #1A1A1A;
  font-weight: 500;
}

.option-count {
  font-size: 11px;
  color: #7A7A7A;
  font-variant-numeric: tabular-nums;
  user-select: none;
  flex-shrink: 0;
}

.option-row-selected .option-count {
  color: #962525;
  font-weight: 600;
}

.section-empty {
  font-size: 11px;
  color: #7A7A7A;
  margin: 4px 0;
  font-style: italic;
}

.section-hint {
  font-size: 11px;
  color: #7A7A7A;
  margin: 8px 0 0;
  padding: 6px 0 2px;
  border-top: 1px dashed #ECECEE;
}

.filter-section-free {
  padding-top: 14px;
}

.free-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3D3D3D;
  margin-bottom: 6px;
  margin-top: 10px;
}

.free-label:first-child {
  margin-top: 0;
}

.free-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #E2E2E2;
  border-radius: 6px;
  font-size: 12px;
  color: #1A1A1A;
  outline: none;
  transition: border-color 180ms cubic-bezier(.2, .7, .3, 1), box-shadow 180ms cubic-bezier(.2, .7, .3, 1);
  box-sizing: border-box;
}

.free-input:focus {
  border-color: #D14040;
  box-shadow: 0 0 0 3px rgba(209, 64, 64, 0.15);
}

.free-input::placeholder {
  color: #A1A1A1;
}

@media (max-width: 1024px) {

  .filters-card {
    border-radius: 0;
  }

  .filters-body {
    max-height: inherit;
  }
}
</style>
